import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { InputAdornment, Stack, TextField } from "@mui/material";
import { GenericFilterMenu } from "src/components/ui/GenericFilterMenu";
import { DocumentsFilterMenuContent } from "./DocumentsFilterMenuContent";
import {
  DocumentsNewAction,
  type DocumentsNewActionProps,
} from "./DocumentsNewAction";
import type { UseDocumentsFiltersResult } from "./useDocumentsFilters";

type DocumentsToolbarProps = DocumentsNewActionProps & {
  filterControls: UseDocumentsFiltersResult;
  onSearchChange: (search: string) => void;
  search: string;
};

export const DocumentsToolbar = ({
  currentDeviceId,
  devices,
  documents,
  filterControls,
  folderId,
  getTemplatesConfigForDevice,
  onSearchChange,
  search,
  templatesConfig,
}: DocumentsToolbarProps) => (
  <Stack
    data-testid="documents-toolbar"
    gap={1.25}
    sx={{
      borderBottom: "1px solid",
      borderColor: "divider",
      boxSizing: "border-box",
      flex: "0 0 42px",
      height: "42px",
      paddingLeft: "10px",
      paddingRight: "7px",
      py: 0,
    }}
  >
    <Stack
      alignItems="center"
      data-testid="documents-toolbar-controls"
      direction="row"
      gap={0.75}
      justifyContent="flex-end"
      sx={{
        height: "100%",
        "& .MuiIconButton-root": {
          color: "text.secondary",
        },
      }}
    >
      <TextField
        fullWidth
        hiddenLabel
        inputProps={{ "aria-label": "Search documents" }}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="Search"
        size="small"
        value={search}
        variant="standard"
        InputProps={{
          disableUnderline: true,
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon
                aria-hidden="true"
                sx={{ color: "rgba(100, 116, 139, 0.9)", fontSize: 16 }}
              />
            </InputAdornment>
          ),
          sx: {
            fontSize: 14,
            "& input": {
              height: 28,
              lineHeight: "28px",
              py: 0,
            },
          },
        }}
      />
      <GenericFilterMenu
        id="documents-filter-menu"
        triggerId="documents-filter-button"
        triggerAriaLabel="Filter documents"
        closeAriaLabel="Close document filters"
        triggerTooltip="Filter documents"
        triggerIcon={<FilterListRoundedIcon fontSize="small" />}
        hasActiveFilters={filterControls.hasActiveFilters}
        triggerSx={{
          backgroundColor: filterControls.hasActiveFilters
            ? "rgba(7, 78, 232, 0.08)"
            : "transparent",
          ...(filterControls.hasActiveFilters ? { color: "primary.main" } : {}),
        }}
        contentTestId="documents-filter-content"
        headerTestId="documents-filter-header"
        bodyTestId="documents-filter-body"
        popperTestId="documents-filter-popper"
        resetButtonTestId="documents-filter-reset"
        placement="bottom-end"
        fallbackPlacements={["bottom-start", "top-end"]}
        onResetFilters={filterControls.actions.resetFilters}
      >
        <DocumentsFilterMenuContent
          actions={filterControls.actions}
          filters={filterControls.filters}
          options={filterControls.options}
          selectedUsers={filterControls.selectedUsers}
        />
      </GenericFilterMenu>
      <DocumentsNewAction
        currentDeviceId={currentDeviceId}
        devices={devices}
        documents={documents}
        folderId={folderId}
        getTemplatesConfigForDevice={getTemplatesConfigForDevice}
        templatesConfig={templatesConfig}
      />
    </Stack>
  </Stack>
);
